import UserInfopreloader from "@components/loader2/UserInfopreloader";
import { USER_TOKEN } from "@constants/auth.constant";
import { IUserInfo } from "@interface/auth.interface";
import { AuthService } from "@services/api/Auth.service";
import * as authHelper from "@services/helper/auth.helper";
import { LocalStorageService } from "@services/utils/localStorage.service";
import { ENV } from "config/ENV.config";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// import { isExpiredToken } from "utility/jwt-token";

const IDLE_LOGOUT_MS = 30 * 60 * 1000; // auto-logout after 30 min of no user activity
const LAST_ACTIVITY_KEY = "lastActivity";

// True when the persisted last-activity timestamp is older than the idle window.
// The timestamp lives in localStorage, so inactivity is honored across full browser/tab
// restarts — an in-memory timer alone would reset on every reload and never expire.
const isIdleExpired = (): boolean => {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!raw) return false; // no record yet — treat an existing session as fresh
  const last = Number(raw);
  if (!last) return false;
  return Date.now() - last > IDLE_LOGOUT_MS;
};

const markActivity = () =>
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));

type AuthContextProps = {
  isAuthenticated: boolean;
  saveAuth: (token: string | undefined, uInfo: IUserInfo | undefined) => void;
  currentUser: IUserInfo | undefined;
  setCurrentUser: Dispatch<SetStateAction<IUserInfo | undefined>>;
  /** Resolves once the server has dropped the token and local auth is cleared. */
  logout: () => Promise<void>;
};

// const isValidToken = () => {
// 	const userToken: string = LocalStorageService.get(USER_TOKEN);
// 	if (!userToken) return false;
// 	console.log(!isExpiredToken(userToken));

// 	return !isExpiredToken(userToken);
// };

const initAuthContextPropsState = {
  isAuthenticated: false,
  saveAuth: () => {},
  currentUser: authHelper.getAuth()?.userInfo,
  setCurrentUser: () => {},
  logout: async () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const [isChecking, setIsChecking] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<IUserInfo | undefined>(
    authHelper.getAuth()?.userInfo
  );
  const [isAuthenticated, makeAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const userToken: string = LocalStorageService.get(USER_TOKEN);
    if (userToken && isIdleExpired()) {
      // Session idled out while the app was closed — clear locally and force re-login.
      // Local-only on purpose: see clearLocalAuth.
      clearLocalAuth();
      setIsChecking(false);
    } else if (userToken) {
      markActivity();
      const productCode = ENV.productCode;
      AuthService.verifyToken(productCode)
        .then((resp) => makeAuthenticated(true))
        // The token was rejected — it is already invalid, so clear locally rather than
        // firing the global revoke at the user's other live sessions.
        .catch(() => clearLocalAuth())
        .finally(() => setIsChecking(false));
    } else setIsChecking(false);
    // eslint-disable-next-line
  }, []);

  // useEffect(() => {
  // 	const interval = setInterval(() => {
  // 		const isValid = isValidToken();
  // 		if (!isValid) {
  // 			makeAuthenticated(isValid);
  // 			logout();
  // 		}
  // 	}, 10000);
  // 	return () => clearInterval(interval);
  // 	// eslint-disable-next-line
  // }, []);

  const saveAuth = (
    token: string | undefined,
    uInfo: IUserInfo | undefined
  ) => {
    setCurrentUser(uInfo);
    if (token) {
      authHelper.setAuth(token, uInfo);
      markActivity();
      makeAuthenticated(true);
    } else {
      authHelper.removeAuth();
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      makeAuthenticated(false);
    }
  };

  // Drop THIS origin's session only. Used for every automatic expiry — the idle timer and a
  // verify-token rejection — where the token is already dead (or dead to us) and there is
  // nothing left to revoke.
  //
  // It must NOT call AuthService.logout(): that endpoint is SINGLE-logout and revokes every
  // token the user holds, across every app. `lastActivity` is per-origin, so a portal tab
  // idling says nothing about an open hrm console — a global revoke from here would sign them
  // out of it mid-work. The token expires on its own (30-min sliding Redis TTL).
  const clearLocalAuth = () => saveAuth(undefined, undefined);

  const logout = () =>
    // Single logout — the user ASKED to sign out, so invalidate the SSO-shared token
    // server-side first (storage still holds it for the axios interceptor), then clear local
    // auth regardless of the call's outcome. Only a deliberate logout belongs here. Callers that
    // wipe more of storage must await this: axios attaches the token in a later microtask.
    AuthService.logout()
      .catch(() => {})
      .finally(() => clearLocalAuth());

  // Auto-logout after IDLE_LOGOUT_MS of no user activity (mouse/keyboard/scroll/touch).
  // Activity bumps a throttled localStorage timestamp (shared across tabs); a poll checks
  // the elapsed time, so idling logs out even after the app was backgrounded or asleep —
  // and one active tab keeps the others alive.
  const idleExpireRef = useRef(clearLocalAuth);
  useEffect(() => {
    idleExpireRef.current = clearLocalAuth;
  });
  const lastBumpRef = useRef(0);
  useEffect(() => {
    if (!isAuthenticated) return;
    const bump = () => {
      const now = Date.now();
      if (now - lastBumpRef.current < 5000) return; // throttle writes to <=1/5s
      lastBumpRef.current = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel", "click"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    markActivity();
    const poll = setInterval(() => {
      if (isIdleExpired()) idleExpireRef.current();
    }, 60 * 1000); // check every minute
    return () => {
      clearInterval(poll);
      events.forEach((e) => window.removeEventListener(e, bump));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (isChecking) return <UserInfopreloader />;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
